import logger from '@/shared/lib/logger';
import { AuthProviderInterface } from './authProvider.interface';
import * as keycloakService from './keycloak';
import * as entraService from './entra';

/**
 * Tipo de provider de autenticação
 */
export type AuthProviderType = 'keycloak' | 'azure' | 'disabled';

/**
 * Determina qual provider de autenticação usar baseado em variáveis de ambiente
 */
export const getAuthProviderType = (): AuthProviderType => {
    // Verifica se a autenticação está desabilitada
    if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
        return 'disabled';
    }

    // Verifica se Azure está configurado
    const azureClientId = import.meta.env.VITE_AZURE_CLIENT_ID;
    const azureTenantId = import.meta.env.VITE_AZURE_TENANT_ID;
    const azureAuthority = import.meta.env.VITE_AZURE_AUTHORITY;

    // Verifica se Keycloak está configurado
    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL;
    const keycloakRealm = import.meta.env.VITE_KEYCLOAK_REALM;
    const keycloakClientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

    // Prioridade: Azure > Keycloak
    // Se Azure estiver configurado, usa Azure
    if (azureClientId || azureTenantId || azureAuthority) {
        logger.debug('Using Azure SSO provider');
        return 'azure';
    }

    // Se Keycloak estiver configurado, usa Keycloak
    if (keycloakUrl && keycloakRealm && keycloakClientId) {
        logger.debug('Using Keycloak SSO provider');
        return 'keycloak';
    }

    // Fallback: tenta usar Keycloak se não houver configuração explícita
    logger.warn('No auth provider explicitly configured, defaulting to Keycloak');
    return 'keycloak';
};

/**
 * Cria uma implementação da interface AuthProviderInterface para Keycloak
 */
const createKeycloakProvider = (): AuthProviderInterface => {
    return {
        init: () => keycloakService.initKeycloak(),
        updateToken: (minValidity = 30) => keycloakService.updateToken(minValidity),
        login: () => keycloakService.loginKeycloak(),
        logout: () => keycloakService.logoutKeycloak(),
        getToken: () => keycloakService.getToken(),
        isAuthenticated: () => keycloakService.isAuthenticated(),
        getUserInfo: () => keycloakService.getUserInfo(),
        getInstance: () => keycloakService.default,
    };
};

/**
 * Cria uma implementação da interface AuthProviderInterface para Azure
 */
const createAzureProvider = (): AuthProviderInterface => {
    return {
        init: () => entraService.initEntra(),
        updateToken: async (minValidity = 30) => {
            const token = await entraService.updateToken(minValidity);
            return !!token; // Convert string | null to boolean
        },
        login: () => entraService.loginEntra(),
        logout: () => entraService.logoutEntra(),
        getToken: () => entraService.getToken(),
        isAuthenticated: () => entraService.isAuthenticated(),
        getUserInfo: () => entraService.getUserInfo(),
        getInstance: () => entraService.default,
    };
};

/**
 * Factory que cria o provider de autenticação apropriado
 */
export const createAuthProvider = (): AuthProviderInterface => {
    const providerType = getAuthProviderType();

    switch (providerType) {
        case 'azure':
            return createAzureProvider();
        case 'keycloak':
            return createKeycloakProvider();
        case 'disabled':
            // Provider dummy para modo de desenvolvimento
            return {
                init: () => Promise.resolve(true),
                updateToken: () => Promise.resolve(true),
                login: () => {
                    logger.debug('Auth disabled, simulating login');
                    window.location.href = window.location.origin;
                },
                logout: () => {
                    logger.debug('Auth disabled, simulating logout');
                    window.location.href = window.location.origin;
                },
                getToken: () => 'dummy-token',
                isAuthenticated: () => true,
                getUserInfo: () => ({
                    sub: 'dummy_sub',
                    id: 'dummy_id',
                    name: 'Usuário Dummy',
                    email: 'dummy@example.com',
                    preferred_username: 'dummyuser',
                    given_name: 'Usuário',
                    family_name: 'Dummy',
                }),
            };
        default:
            logger.warn('Unknown auth provider type, defaulting to Keycloak');
            return createKeycloakProvider();
    }
};

/**
 * Obtém o tipo de provider atual (útil para componentes que precisam saber qual provider está ativo)
 */
export const getCurrentAuthProviderType = (): AuthProviderType => {
    return getAuthProviderType();
};
